import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  CalendarClock, FileSpreadsheet, Users, ArrowRight, Inbox, AlertCircle, MessageSquare,
  RefreshCw,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_ICON = {
  appointment: CalendarClock,
  claim:       FileSpreadsheet,
  referral:    Inbox,
  clinician:   Users,
  message:     MessageSquare,
  alert:       AlertCircle,
};

const ACCENT_BG = {
  pine:   'bg-cn-primary/10 text-cn-primary',
  amber:  'bg-amber-50 text-amber-700',
  sage:   'bg-cn-sage-100 text-cn-primary',
  terra:  'bg-cn-terracotta-50 text-cn-terracotta-600',
};

/**
 * Morning Today widget — Blueprint Decision #18.
 *
 * Drops into a role-aware dashboard. Compresses decision space by
 * surfacing 3-5 ritual items + 1-3 KPI counters.
 *
 * Self-contained: fetches `/api/today` on mount, handles loading + empty
 * + error states. Auth is the standard Bearer-token interceptor.
 */
export const MorningTodayWidget = ({ getAuthHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/today`, getAuthHeader ? getAuthHeader() : {});
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Could not load Today');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) {
    return (
      <section className="cn-card p-6 animate-pulse bg-cn-bg/60" data-testid="today-widget-skeleton">
        <div className="h-5 w-44 bg-cn-bone-200 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => <div key={i} className="h-12 bg-cn-bone-200/70 rounded-lg" />)}
        </div>
      </section>
    );
  }

  if (err) {
    return (
      <section className="cn-card p-6 border-amber-200 bg-amber-50/30" data-testid="today-widget-error">
        <div className="flex items-center gap-2 text-amber-700">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-semibold">Today view unavailable</span>
        </div>
        <p className="text-xs text-cn-text-muted mt-1">{err}</p>
      </section>
    );
  }

  return (
    <section className="cn-card p-6" data-testid="today-widget">
      <header className="flex items-center justify-between mb-4">
        <div>
          <div className="cn-eyebrow mb-1">Today</div>
          <h2
            className="font-display font-bold text-xl text-cn-text dark:text-white"
            data-testid="today-greeting"
          >
            {data?.greeting || 'Today'}
          </h2>
        </div>
        <button
          type="button"
          onClick={load}
          className="cn-btn cn-btn-ghost !py-2 !px-3"
          aria-label="Refresh today view"
          data-testid="today-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      {/* counters */}
      {data?.counters?.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-5" data-testid="today-counters">
          {data.counters.map((c, i) => (
            <div
              key={c.label}
              className={`rounded-xl px-3 py-2.5 ${ACCENT_BG[c.accent] || ACCENT_BG.pine}`}
              data-testid={`today-counter-${i}`}
            >
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{c.label}</div>
              <div className="font-display font-bold text-xl mt-0.5">{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* items */}
      {data?.items?.length > 0 ? (
        <ul className="space-y-2.5" data-testid="today-items">
          {data.items.map((it, i) => {
            const Icon = KIND_ICON[it.kind] || ArrowRight;
            return (
              <li key={i}>
                <Link
                  to={it.href}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5 border border-cn-border hover:border-cn-primary/40 hover:bg-cn-bone-100/30 transition-colors group"
                  data-testid={`today-item-${i}`}
                >
                  <span className="w-9 h-9 rounded-lg bg-cn-primary/10 text-cn-primary flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-cn-text dark:text-white truncate">{it.title}</div>
                    {it.subtitle && (
                      <div className="text-xs text-cn-text-muted truncate">{it.subtitle}</div>
                    )}
                  </div>
                  <ArrowRight className="w-4 h-4 text-cn-text-muted/60 group-hover:text-cn-primary transition-colors shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-sm text-cn-text-muted py-4" data-testid="today-empty">
          {data?.empty_state || 'Nothing urgent. Take a breath.'}
        </p>
      )}
    </section>
  );
};

export default MorningTodayWidget;
